import { useMutation, useQueryClient } from "@tanstack/react-query";
import { vaultApi } from "../../../api/vault.api";
import { vaultKeys } from "../queryKeys";
import { getExceptionMessage } from "../../../exceptions/helpers/getExceptionMessage";
import { vaultExceptions } from "../../../exceptions/default/vaultExceptions";

interface UpdateWorkflowStepParams {
  reqId: string;
  workflow_step: string;
  confirmed_data?: Record<string, any>;
}

export interface UpdateWorkflowStepError extends Error {
  code?: unknown;
  field_errors?: unknown;
  target_slug?: unknown;
}

export function useUpdateWorkflowStep(folderId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      reqId,
      workflow_step,
      confirmed_data,
    }: UpdateWorkflowStepParams) => {
      try {
        return await vaultApi.updateWorkflowStep(reqId, {
          workflow_step,
          confirmed_data,
        });
      } catch (error: any) {
        const responseData = error?.response?.data;
        const fieldMessage =
          responseData?.code === "WS002" &&
          responseData.field_errors &&
          typeof responseData.field_errors === "object"
            ? Object.values(responseData.field_errors)
                .filter(
                  (value): value is string =>
                    typeof value === "string" && Boolean(value.trim()),
                )
                .join(" ")
            : "";
        const preservedError = new Error(
          fieldMessage ||
            getExceptionMessage(
              responseData?.code,
              vaultExceptions.updateVaultFormValues,
            ),
        ) as UpdateWorkflowStepError;
        preservedError.code = responseData?.code ?? error?.code;
        preservedError.field_errors = responseData?.field_errors;
        preservedError.target_slug = responseData?.target_slug;
        throw preservedError;
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: vaultKeys.folder(folderId ?? ""),
      });
    },
  });
}
